import { isTokenValid, getStoredIdToken } from '../auth-config.js';

/** Pure decision: what should the gate do given current state? */
export function decideGate({ hasValidToken, skip }) {
  if (hasValidToken) return 'authed';
  if (skip) return 'skip';
  return 'show';
}

// Skip is PER-VISIT (owner decision 2026-07-12): "Skip for testing" must be re-clicked every
// visit. The flag lives in sessionStorage — it survives the intake→results navigation within a
// tab but dies with it — and the ?skipRegistration=1 test hook stays per-load. The key name is
// shared with onboarding.js's SKIP_KEY so both skips behave identically.
const SKIP_KEY = 'wq_skip_registration_local';

// Sign-in is REQUIRED on the live site (owner decision 2026-10-22, plan advisor release): the
// company rules only reach a signed-in agent. Skipping is a test hook for a local server only.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
export function skipAllowed(hostname) {
  return LOCAL_HOSTS.has(String(hostname || '').toLowerCase());
}

function isSkip() {
  try {
    if (!skipAllowed(window.location.hostname)) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('skipRegistration') === '1') return true;
    return sessionStorage.getItem(SKIP_KEY) === '1';
  } catch { return false; }
}

/** Read real state and route to the matching callback. */
export function run({ onAuthed, onSkip, onShow }) {
  // Scrub the legacy PERSISTENT skip (pre-2026-07-12 localStorage flag) so browsers that ever
  // clicked the old "Skip for testing" — i.e., every pre-auth user — get gated again.
  try { localStorage.removeItem(SKIP_KEY); } catch { /* noop */ }
  const state = decideGate({ hasValidToken: isTokenValid(getStoredIdToken()), skip: isSkip() });
  if (state === 'authed') return onAuthed && onAuthed();
  if (state === 'skip') return onSkip && onSkip();
  return onShow && onShow();
}

// Note: in Safari private windows / restricted contexts sessionStorage writes can throw — the
// catch means skip silently doesn't stick there and the gate re-shows next load. Acceptable.
export function markSkip() { try { sessionStorage.setItem(SKIP_KEY, '1'); } catch {} }

export const AuthGate = { decideGate, run, markSkip, skipAllowed };
